import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import * as CM from 'app/cm/models/essette-api';
import { apiCall } from 'app/shared/lib/essette-api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';

@autoinject()
export class CaseCheckoutHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    protected checkouts: ReadonlyArray<CM.CaseCheckoutHistory> | undefined = undefined;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected showCancelAction: boolean = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
        this.checkPermissions();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCheckoutHistory();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.showCancelAction = this.accountContext.hasPermission('CM.CaseCheckoutHistory.Deactivate.CancelCaseCheckout');
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCheckoutHistory(): Promise<void> {
        if(!this.caseId) return;

        const includes: string[] = [
            'metadata.started.byUser',
            'metadata.ended.byUser',
            'metadata.cancelled.byUser',
            'case'
        ];

        const fields: string[] = [
            'case.number',
            'isActive',
            'metadata.cancelled.byUser.name.full',
            'metadata.cancelled.timestamp',
            'metadata.ended.byUser.description',
            'metadata.ended.timestamp',
            'metadata.started.byUser.description',
            'metadata.started.timestamp',
        ];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadCaseCheckoutHistory(this.caseId, options, includes, fields);
            this.checkouts = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async cancelCheckout(checkout: CM.CaseCheckoutHistory): Promise<void> {
        try {
            this.state.beginSave();
            const response = await this.service.cancelCheckout(checkout);
            this.toaster.standard.activate('cm:caseCheckout.$', false, response.isSuccessful);
            this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
